import { lazy, Suspense, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Message } from "@arco-design/web-react";
import { Logo } from "./components/Logo";
import { api, post, setCsrf, showError } from "./api";
import { Auth } from "./Auth";
import { ActivateAccess } from "./ActivateAccess";
import type { Account } from "../shared/domain";
import { useLocation } from "react-router-dom";

const SkillPage = lazy(() => import("./pages/Skill").then(m => ({ default: m.SkillPage })));
const AgentConsent = lazy(() => import("./components/AgentAccess").then(m => ({ default: m.AgentConsent })));
const Workspace = lazy(() => import("./Workspace").then(m => ({ default: m.Workspace })));
function Loading() {
  return <div className="app-loading"><span className="brand-mark"><Logo /></span><p>打开你的私人空间…</p></div>;
}

export function App() {
  const { pathname } = useLocation();
  if (pathname === "/skill" || pathname === "/skill/")
    return <Suspense fallback={<Loading />}><SkillPage /></Suspense>;
  return <AccountApp />;
}

function AccountApp() {
  const client = useQueryClient(),
    query = useQuery({
      queryKey: ["me"],
      queryFn: async () => {
        const result = await api<{ account: Account; csrf: string }>(
          "/auth/me",
        );
        setCsrf(result.csrf);
        return result;
      },
      retry: false,
    });
  useEffect(() => {
    const token = new URLSearchParams(location.search).get("verify");
    if (token)
      post("/auth/verify-email", { token })
        .then(() => {
          Message.success("邮箱已验证");
          history.replaceState(null, "", "/");
          client.invalidateQueries();
        })
        .catch(showError);
  }, []);
  if (query.isPending)
    return (
      <div className="app-loading">
        <span className="brand-mark">
          <Logo />
        </span>
        <p>打开你的私人空间…</p>
      </div>
    );
  if (!query.data)
    return (
      <Auth
        onLogin={() => {
          client.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
          query.refetch();
        }}
      />
    );
  if (!query.data.account.hasAccess)
    return <ActivateAccess account={query.data.account} onRefresh={() => {
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      query.refetch();
    }} onLogout={() => {
      client.setQueryData(["me"], null);
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      query.refetch();
    }} />;
  return <Suspense fallback={<Loading />}>{location.pathname === "/agent/authorize" ? <AgentConsent /> : <Workspace />}</Suspense>;
}
